import { ButtonSpinner } from "@/components/ButtonSpinner";
import { Button } from "@/components/ui/button";
import { Dropdown } from "@/crud/form/Dropdown";
import { Input } from "@/crud/form/Input";
import { Quill } from "@/crud/form/Quill/Quill";
import { Textarea } from "@/crud/form/Textarea";
import yup from "@/crud/yup-extended";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import { StepRun } from "@/models/flows";
import axios from "axios";
import { Field, Formik, FormikValues, getIn } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";

interface Props {
  stepRun: StepRun;
  override?: boolean; // Optional prop to override the default behavior
  onDecisionSuccess?: () => void; // Callback for when decision is successfully submitted
}

export const ApprovalStep = ({
  stepRun,
  override,
  onDecisionSuccess,
}: Props) => {
  const darkMode = useDarkMode();
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const decisions = [
    {
      id: "approved",
      name: "Approve",
      description: "Approve this decision and move to the approval step",
    },
    {
      id: "declined",
      name: "Decline",
      description: "Decline this decision and move to the decline step",
    },
  ];

  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (formData: FormikValues) => {
      if (override) {
        formData["override"] = true; // Add override flag if needed
      }
      return await axios.post(
        `${BASE_URL}/step-runs/${stepRun.id}/decision`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["flowRun", stepRun.flowRun]); // Invalidate the selected flow run
        // invalidate step-runs too maybe to reflect it now being completed
        setIsSubmittingDecision(false);
        onDecisionSuccess();
      },
    }
  );

  // Find group_selector field if present
  const groupSelectorField = stepRun.renderedConfig.fields?.find(
    (field) => field.systemType === "group_selector"
  );

  // Fetch groups if group_selector is present
  const {
    data: groupOptions,
    isLoading: isGroupsLoading,
    isError: isGroupsError,
  } = useQuery(
    ["accreditationGroups"],
    async () => {
      const response = await axios.get(`${BASE_URL}/accreditation/groups/list`);
      return response.data;
    },
    {
      enabled: !!groupSelectorField,
    }
  );

  return (
    <div>
      {stepRun.status === "waiting" || override ? (
        <Formik
          initialValues={{
            decision: null,
            reason: "",
            ...stepRun.renderedConfig.fields?.reduce(
              (acc, field) => ({ ...acc, [field.name]: field.default }),
              {}
            ),
          }}
          validationSchema={yup.object({
            decision: yup
              .string()
              .required("Please select a decision")
              .oneOf(decisions.map((decision) => decision.id)),
            reason: yup.string().optional(),
          })}
          onSubmit={async (values) => {
            setIsSubmittingDecision(true);
            // Convert group_selector value to just the ID before submit
            const submitValues = { ...values };
            stepRun.renderedConfig.fields?.forEach((field) => {
              if (
                field.systemType === "group_selector" &&
                submitValues[field.name]
              ) {
                // If Dropdown value is an object, extract the id
                if (
                  typeof submitValues[field.name] === "object" &&
                  submitValues[field.name] !== null &&
                  "id" in submitValues[field.name]
                ) {
                  submitValues[field.name] = submitValues[field.name].id;
                }
              }
            });
            await mutation.mutate(submitValues);
          }}
        >
          {({ handleSubmit, errors, isSubmitting, values, setFieldValue }) => (
            <form>
              {stepRun.renderedConfig.fields && (
                <div className="mt-4 border-b pb-2 dark:border-white/10">
                  <div className="flex">
                    <div className="w-1/2">
                      <h4 className="dark:text-zinc-500 mb-2 text-sm">
                        Override fields
                      </h4>
                    </div>
                  </div>
                  {stepRun.renderedConfig.fields.map((field, index) => {
                    // Default input for other fields
                    return (
                      <div className="mb-2" key={field.name}>
                        <div className="flex items-center">
                          <div className="w-1/2 text-sm dark:text-white">
                            {field.label}
                          </div>
                          <div className="w-1/2">
                            {field.type === "data" ? (
                              <Input
                                name={field.name}
                                required
                                type="text"
                                value={getIn(values, field.name)}
                              />
                            ) : (
                              <>
                                {field.systemType === "group_selector" &&
                                  groupOptions && (
                                    <Dropdown
                                      name={field.name}
                                      options={groupOptions}
                                    />
                                  )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-3">
                <h4 className="dark:text-zinc-500 mb-2 text-sm">Reason</h4>
                <Textarea
                  required={false}
                  name="reason"
                  placeholder="Give an optional reason for your decision"
                />
                <div className="mt-4 ml-1 mb-1">
                  <fieldset aria-label="Plan">
                    <div className="space-y-2">
                      {decisions.map((decision) => (
                        <div
                          key={decision.id}
                          className="relative flex items-start"
                        >
                          <div className="flex h-6 items-center">
                            <Field
                              id={decision.id}
                              name="decision"
                              value={decision.id}
                              type="radio"
                              aria-describedby={`${decision.id}-description`}
                              className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                            />
                          </div>
                          <div className="ml-3 text-sm/6">
                            <label
                              htmlFor={decision.id}
                              className="font-medium text-gray-900 dark:text-white"
                            >
                              {decision.name}
                            </label>
                            <p
                              id={`${decision.id}-description`}
                              className="text-gray-500 dark:text-zinc-500"
                            >
                              {decision.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.decision && (
                      <p className="mt-3 text-sm text-red-600 dark:text-red-500">
                        {errors.decision}
                      </p>
                    )}
                  </fieldset>
                </div>
                <Button
                  type="button"
                  className="mt-4"
                  variant={darkMode ? "secondary" : "default"}
                  size="sm"
                  onClick={() => handleSubmit()}
                >
                  {isSubmittingDecision ? (
                    <>
                      <ButtonSpinner colour={darkMode ? "black" : "white"} />
                      Submitting review...
                    </>
                  ) : (
                    "Submit review"
                  )}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <>
          {stepRun.status === "completed" ? (
            <>
              <span className="dark:text-white mr-1">Decision was:</span>{" "}
              {stepRun.outputData.decision === "approved" ? (
                <span className="inline-flex items-center rounded-md bg-green-500/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-500/20">
                  Approved
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-inset ring-red-400/20">
                  Declined
                </span>
              )}
            </>
          ) : (
            <>step {stepRun.status}</>
          )}
        </>
      )}
    </div>
  );
};
